import { useCallback, useEffect, useMemo } from 'react';

import { Feature, FeatureCollection } from 'geojson';
import L, { LatLngExpression } from 'leaflet';
import { nanoid } from 'nanoid';
import numeral from 'numeral';
import { GeoJSON, MapContainer, useMap } from "react-leaflet";
import { detaultMapCenter } from '../context/map-data';
import { Bounds } from '../core/global';
import { convertGeomToGeoJSON, isNumber } from '../helpers/core';
import styles from "../styles/components/selected-area-layer.module.scss";
import { CurrentMap } from "./layers-toggle";
import { ProjectPoint } from "./map-project-points";
import { SelectedAreaLayer } from './selected-area-layer';
import { MapBoundsUpdateDescendant } from './styled-map-container';

interface UpdateMapPositionProps {
  lat: string | number
  lon: string | number
}

const UpdateMapPosition = ({lat, lon}: UpdateMapPositionProps) => {
  const map = useMap();

  useEffect(() => {
    if (map && isNumber(lat) && isNumber(lon)) {
      // const zoomLevel = map.getZoom()
      // map.setView([Number(lat), Number(lon)], zoomLevel > 9 ? zoomLevel : 9, {animate: false})
      map.setView([Number(lat), Number(lon)], undefined, {animate: false})
    }
  }, [lat, lon, map])

  return null;
}

interface MapPreviewProps {
  lat?: number | string
  lon?: number | string
  width?: string | number
  onClick?: (lat: string, lon: string) => void
  onChangeMapLocation?: (lat: number, lon: number, bounds?: Bounds | null, zoom?: number | null) => void
  showPoint?: boolean
  zoom?: number | null
  bounds?: number[] | null
  overlayPolygon?: string | null
  showROI?: boolean
  overlayPolygonProperties?: any    // 
}

export const MapPreview = ({
  lat,
  lon,
  width = "350",
  onClick,
  onChangeMapLocation,
  showPoint = true,
  zoom = null,
  bounds = null,
  overlayPolygon = null,
  showROI = false,
  overlayPolygonProperties = {}
}: MapPreviewProps) => {
  const hasLocation = !!(isNumber(lat) && isNumber(lon))
  const position: LatLngExpression | undefined = !bounds ? detaultMapCenter : undefined

  const features:(FeatureCollection | null) = useMemo(() => {
    let features: Feature[] = []
    if (overlayPolygon) {
      features = [convertGeomToGeoJSON(overlayPolygon, overlayPolygonProperties)]
    }

    if (features.length > 0) {
      return {
        "type": "FeatureCollection",
        "features": features
      }  
    }
    return null
  }, [overlayPolygon, overlayPolygonProperties])

  const styleFeature = useCallback((feature: any) => {
    const fillColor = '#95DDAA'
    
    // const featureColor = feature?.properties?.color || '#ffffff'

    const isPoint = feature.geometry.type === 'Point'
    const isLine = feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString'
    if (isPoint) {
      return {
        fill: true,
        stroke: true,
        fillColor: feature?.properties?.color || "#FF7F01",
        color: "#ffffff",
        weight: 3,
        fillOpacity: 1
      }
    }

    const useColour = '#000000';

    const showFill = fillColor ? true : false
    return {
      filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 1))',
      // color: !showFill ? useColour : '',
      color: feature?.properties?.color || useColour,
      stroke: true,
      fill: showFill,
      fillColor,
      weight: 2,
      opacity: 1,
      fillOpacity: !isLine? 0.2: 0,
    }
  }, [])

  return (
    <MapContainer
      style={{ width: `${width}px`, height: "200px" }}
      center={(hasLocation && !bounds) ? [Number(lat), Number(lon)] : position}
      bounds={bounds ? [[bounds[0], bounds[1]], [bounds[2], bounds[3]]] : undefined}
      boundsOptions={bounds ? { padding: [10, 10] } : undefined}
      zoom={zoom ? zoom : hasLocation ? 6 : 2}
      minZoom={2}
      scrollWheelZoom={true}
      maxZoom={Number(import.meta.env.VITE_MAX_ZOOM || 11)}
    >
      {showROI && <SelectedAreaLayer />}
      <CurrentMap mapProvider="openstreetmap" updateLocationOnMapChange={false} />
      {showPoint && hasLocation && <ProjectPoint point={[Number(lat), Number(lon)]} iconColor="yellow" />}
      {/* <MouseCoordinates className={styles.coords} /> */}
      {isNumber(lat) && isNumber(lon) && <UpdateMapPosition lat={lat} lon={lon} />}
      <MapBoundsUpdateDescendant 
        onChange={(lat, lon) => {
          if (isNumber(lat) && isNumber(lon) && onClick) {
            onClick(numeral(Number(lat)).format('0.00'), numeral(Number(lon)).format('0.00'))
          }
        }}
        onChangeMapLocation={(lat, lon, bounds, zoom) => {
          if (onChangeMapLocation) {
            onChangeMapLocation(lat, lon, bounds, zoom)
          }
        }}
      />
      {features && 
        <GeoJSON
          // @ts-ignore 
          key={nanoid()}
          attribution=""
          data={features}
          style={styleFeature}
          pointToLayer={(feature, point) => {
            return L.circleMarker(point, {
              radius: 6
            });
          }}
          onEachFeature={(feature: any, layer: any) => {
            if (feature.properties.name || feature.properties.NAME) {
              layer.bindTooltip(
                feature.properties.name || feature.properties.NAME || '',
                {
                    className: styles.shapeLabel
                }
              );
            }
            // layer.on('mouseout', () => {})
          }}
        />
      }
    </MapContainer>
  )
}
